import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import { CalendarClock, LucideIconData } from 'lucide-angular';

import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from '../../../shared/ui/breadcrumb/breadcrumb.component';
import { ButtonComponent } from '../../../shared/ui/button/button.component';
import { IconComponent } from '../../../shared/ui/icon/icon.component';
import { ProductCardComponent } from '../../../shared/ui/product-card/product-card.component';
import { ProductCardSkeletonComponent } from '../../../shared/ui/product-card-skeleton/product-card-skeleton.component';
import { ProductZoomModalComponent } from '../../../shared/ui/product-zoom-modal/product-zoom-modal.component';
import { PrepurchaseService } from '../../../shared/services/prepurchase.service';
import { PrepurchaseModalService } from '../../../shared/services/prepurchase-modal.service';
import { AddToCartEvent, Product } from '../../../shared/models/product.model';
import { deriveProductCategorySlug } from '../../../features/catalog/data/catalog-mappers';
import { toSignal } from '@angular/core/rxjs-interop';
import { CategoryService } from '../../../shared/services/category.service';
import { Category } from '../../../shared/models/category.model';

/**
 * Página dedicada a listar productos en precompra. Canal de entrada directo
 * desde el navbar y desde el megamenú sintético de Tienda.
 *
 * No reutilizamos `CatalogPageComponent` porque:
 *  - No tiene filtros ni facetas (el endpoint `/prepurchase-products` devuelve
 *    un set pequeño y sin paginar).
 *  - El hero y la copy son diferentes (explica qué es una precompra y el 30%).
 *  - El CTA primario de cada card va al modal de precompra, no al carrito.
 */
@Component({
  selector: 'app-prepurchase-list-page',
  imports: [
    BreadcrumbComponent,
    ButtonComponent,
    IconComponent,
    ProductCardComponent,
    ProductCardSkeletonComponent,
    ProductZoomModalComponent,
  ],
  templateUrl: './prepurchase-list-page.component.html',
  styleUrl: './prepurchase-list-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrepurchaseListPageComponent {
  private readonly prepurchaseService = inject(PrepurchaseService);
  private readonly prepurchaseModalService = inject(PrepurchaseModalService);
  private readonly categoryService = inject(CategoryService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly calendarIcon: LucideIconData = CalendarClock;

  protected readonly products = signal<Product[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly zoomProduct = signal<Product | null>(null);

  protected readonly skeletonArray = Array.from({ length: 8 }, (_, i) => i);

  protected readonly breadcrumbs: BreadcrumbItem[] = [
    { label: 'Inicio', route: '/' },
    { label: 'Tienda', route: '/tienda' },
    { label: 'Precompras', route: null },
  ];

  // Árbol de categorías para derivar el slug cuando el user hace click en una card.
  private readonly rootCategoriesSig = toSignal(
    this.categoryService.getRootCategoriesCached(),
    { initialValue: [] as Category[] },
  );

  constructor() {
    this.fetchProducts();
  }

  protected onRetry(): void {
    this.fetchProducts();
  }

  protected onReserve(product: Product): void {
    this.prepurchaseModalService.open(product);
  }

  protected onZoomOpen(product: Product): void {
    this.zoomProduct.set(product);
  }

  protected onZoomClose(): void {
    this.zoomProduct.set(null);
  }

  /**
   * Handler del `(reserveRequest)` del ProductZoomModal cuando el producto
   * es pre-sale. Cierra el zoom y abre el modal de precompra con el qty
   * que el usuario eligió en el zoom.
   */
  protected onZoomReserve(event: AddToCartEvent): void {
    this.zoomProduct.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  /**
   * En esta página todo es pre-sale por definición, así que el ZoomModal
   * nunca debería emitir `addToCart` — pero dejamos un fallback defensivo
   * que redirige al modal correcto si alguna vez pasa.
   */
  protected onZoomAddToCart(event: AddToCartEvent): void {
    this.zoomProduct.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  protected onViewDetails(product: Product): void {
    this.zoomProduct.set(null);
    this.onCardClick(product);
  }

  protected onCardClick(product: Product): void {
    if (!product.slug) return;
    const slug = deriveProductCategorySlug(product, this.rootCategoriesSig());
    if (!slug) return;
    this.router.navigate(['/tienda', slug, product.slug]);
  }

  private fetchProducts(): void {
    this.loading.set(true);
    this.error.set(null);

    this.prepurchaseService
      .getPrepurchaseProducts()
      .pipe(
        catchError((err) => {
          console.error('[prepurchase-list] fetch failed', err);
          this.error.set('No pudimos cargar las precompras. Inténtalo de nuevo.');
          return of<Product[]>([]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((products) => {
        this.products.set(products);
        this.loading.set(false);
      });
  }
}
